import { CustomTheme } from './CustomTheme';

const GROMMET_BREAKPOINTS = {
  // Map a set of our custom breakpoints to grommet.
  // These are offset a breakpoint as grommet uses max-width queries,
  // and our custom breakpoints use min-width queries.
  small: {
    value: CustomTheme.breakpoints.medium - 1,
  },
  medium: {
    value: CustomTheme.breakpoints.large - 1,
  },
  large: {
    value: CustomTheme.breakpoints.xlarge - 1,
  },
};

// === COLORS ===
const ACCENT_COLORS = {
  'accent-1': '#EB8C00',
  'accent-2': '#FFB600',
  'accent-3': '#993803', // darker brand, for hovers
};
const NEUTRAL_COLORS = {
  'neutral-1': '#295477',
  'neutral-2': '#299D8F',
};
const DARK_COLORS = {
  'dark-0': '#000000',
  'dark-1': '#7D7D7D',
  'dark-2': '#404041',
  'dark-3': '#2D2D2D',
  'dark-4': '#464646',
};
const LIGHT_COLORS = {
  'light-1': '#FFFFFF',
  'light-2': '#F2F2F2',
  'light-3': '#DEDEDE',
  'light-4': '#C4C4C4',
};

const STATUS_COLORS = {
  'status-error': '#E0301E',
  'status-ok': '#1AAA55',
  'status-focus': '#4590FF',
  'status-warning': '#FFCC01',
};
const GROMMET_COLORS = {
  text: {
    dark: '#000',
    light: '#fff',
  },
  brand: '#D04A02',
  ...ACCENT_COLORS,
  ...NEUTRAL_COLORS,
  ...STATUS_COLORS,
  ...DARK_COLORS,
  ...LIGHT_COLORS,
};

// === GRID SETTINGS ===

const GROMMET_SPACING = {
  none: '0px',
  hair: '1px',
  xxsmall: '3px',
  xsmall: '10px',
  small: `${CustomTheme.grid.small.gutter}px`,
  medium: `${CustomTheme.grid.medium.gutter}px`,
  large: `${CustomTheme.grid.large.gutter}px`,
  xlarge: '40px',
};

// === TYPOGRAPHY ===

// The style guide defines things in px, but the codebase needs to use REMs
// to facilitate ensuring we're using the right values from the style guide, we // use a function to do the REM math.

const GROMMET_HEADING = {
  color: GROMMET_COLORS.text.dark,
  responsiveBreakpoint: 'small',
  level: {
    1: {
      font: { weight: 400, family: CustomTheme.type.serif },
      medium: {
        size: CustomTheme.helpers.pxToRem(40),
        height: CustomTheme.helpers.pxToRem(48),
        maxWidth: '100%',
        letterSpacing: '.20px',
      },
      small: {
        size: CustomTheme.helpers.pxToRem(28),
        height: CustomTheme.helpers.pxToRem(32),
        maxWidth: '100%',
        letterSpacing: '.20px',
      },
    },
    2: {
      font: { weight: 400, family: CustomTheme.type.serif },
      medium: {
        size: CustomTheme.helpers.pxToRem(36),
        height: CustomTheme.helpers.pxToRem(48),
        maxWidth: '100%',
        letterSpacing: '.20px',
      },
      small: {
        size: CustomTheme.helpers.pxToRem(30),
        height: CustomTheme.helpers.pxToRem(38),
        maxWidth: '100%',
        letterSpacing: '.20px',
      },
    },
    3: {
      font: { weight: 700, family: CustomTheme.type.sans },
      medium: {
        size: CustomTheme.helpers.pxToRem(28),
        height: CustomTheme.helpers.pxToRem(36),
        maxWidth: '100%',
        letterSpacing: '.20px',
      },
      small: {
        size: CustomTheme.helpers.pxToRem(28),
        height: CustomTheme.helpers.pxToRem(40),
        maxWidth: '100%',
        letterSpacing: '.20px',
      },
    },
    4: {
      font: { weight: 400, family: CustomTheme.type.serif },
      medium: {
        size: CustomTheme.helpers.pxToRem(24),
        height: CustomTheme.helpers.pxToRem(36),
        maxWidth: '100%',
        letterSpacing: '.20px',
      },
      small: {
        size: CustomTheme.helpers.pxToRem(24),
        height: CustomTheme.helpers.pxToRem(36),
        maxWidth: '100%',
        letterSpacing: '.20px',
      },
    },
    5: {
      font: {
        weight: 700,
        family: CustomTheme.type.sans,
      },
      medium: {
        size: CustomTheme.helpers.pxToRem(20),
        maxWidth: '100%',
        letterSpacing: '.20px',
        height: '32px',
      },
      small: {
        size: CustomTheme.helpers.pxToRem(20),
        maxWidth: '100%',
        letterSpacing: '.20px',
        height: '24px',
      },
    },
    6: {
      font: {
        weight: 700,
        family: CustomTheme.type.sans,
      },
      medium: {
        size: CustomTheme.helpers.pxToRem(18),
        height: CustomTheme.helpers.pxToRem(20),
        maxWidth: '100%',
        letterSpacing: '.20px',
      },
      small: {
        size: CustomTheme.helpers.pxToRem(18),
        height: CustomTheme.helpers.pxToRem(20),
        maxWidth: '100%',
        letterSpacing: '.20px',
      },
    },
  },
};

const GROMMET_PARAGRAPH = {
  color: GROMMET_COLORS.text.dark,
  font: { weight: '400', family: CustomTheme.type.sans },
  large: {
    size: CustomTheme.helpers.pxToRem(24),
    height: CustomTheme.helpers.pxToRem(36),
    letterSpacing: 'normal !important',
    maxWidth: '100%',
  },
  medium: {
    size: CustomTheme.helpers.pxToRem(18),
    height: CustomTheme.helpers.pxToRem(28),
    letterSpacing: 'normal !important',
    maxWidth: '100%',
  },
  small: {
    size: CustomTheme.helpers.pxToRem(16),
    height: CustomTheme.helpers.pxToRem(28),
    maxWidth: '100%',
  },
  xsmall: {
    size: CustomTheme.helpers.pxToRem(12),
    height: CustomTheme.helpers.pxToRem(18),
    maxWidth: '100%',
  },
};

const GROMMET_TEXT = {
  ...GROMMET_PARAGRAPH,
};

// === THEME DEFINITIONS

const GrommetTheme = {
  rounding: 0,
  spacing: 24,
  defaultMode: 'light',
  global: {
    focus: {
      border: {
        color: 'transparent',
      },
    },
    colors: { ...GROMMET_COLORS },
    breakpoints: { ...GROMMET_BREAKPOINTS },
    edgeSize: { ...GROMMET_SPACING },
  },
  heading: { ...GROMMET_HEADING },
  paragraph: { ...GROMMET_PARAGRAPH },
  text: { ...GROMMET_TEXT },
};

export default GrommetTheme;
